import React, { useEffect, useMemo, useState } from 'react';
import TimeZoneCombobox from '../TimeZoneCombobox';
import sandboxApi from '../../utils/api/sandboxApi';

const emptyForm = {
    practiceName: '',
    timeZoneId: '',
    officeEmail: '',
    officePhone: '',
    officeAddress: '',
    officeCity: '',
    officeState: '',
    officeZip: '',
};

function getBrowserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
}

function getErrorMessage(error) {
    const rawMessage = error?.message || '';
    let message = rawMessage;

    try {
        const parsed = JSON.parse(rawMessage);
        message = parsed?.message || parsed?.detail || parsed?.title || rawMessage;
    } catch {
        // API helpers may throw either plain text or a JSON string.
    }

    const normalized = message.toLowerCase();
    if (normalized.includes('not') && normalized.includes('sandbox')) {
        return 'This account is no longer in sandbox mode. Refresh the page and try again.';
    }
    if (normalized.includes('already') && normalized.includes('convert')) {
        return 'This sandbox has already been converted to a live practice. Refresh the page to continue.';
    }
    if (normalized.includes('practice') && normalized.includes('name')) {
        return 'Practice name is required to start your real practice.';
    }
    if (normalized.includes('unauthorized') || normalized.includes('forbidden') || normalized.includes('session expired')) {
        return 'You are not authorized to convert this sandbox. Please sign in again or contact support.';
    }

    return message || 'Unable to start your real practice. Please try again.';
}

export default function SandboxConversionModal({ isOpen, organization, onClose, onConverted }) {
    const initialForm = useMemo(() => ({
        ...emptyForm,
        timeZoneId: organization?.timeZoneId || organization?.TimeZoneId || getBrowserTimeZone(),
        // Sandbox contact details belong to the demo template. Starting a live
        // practice is a clean boundary, so require the user to enter real values.
        officeEmail: '',
        officePhone: '',
    }), [organization]);

    const [formData, setFormData] = useState(initialForm);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFormData(initialForm);
            setError('');
        }
    }, [initialForm, isOpen]);

    if (!isOpen) return null;

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const practiceName = formData.practiceName.trim();
        const timeZoneId = formData.timeZoneId.trim();

        if (!practiceName) {
            setError('Practice name is required to start your real practice.');
            return;
        }
        if (!timeZoneId) {
            setError('Time zone is required to start your real practice.');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            const payload = {
                practiceName,
                name: practiceName,
                timeZoneId,
                officeEmail: formData.officeEmail.trim() || null,
                officePhone: formData.officePhone.trim() || null,
                officeAddress: formData.officeAddress.trim() || null,
                officeCity: formData.officeCity.trim() || null,
                officeState: formData.officeState.trim() || null,
                officeZip: formData.officeZip.trim() || null,
            };

            const result = await sandboxApi.convertToLive(payload);
            await onConverted?.(result);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="sandbox-conversion-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-5">
                    <h2 id="sandbox-conversion-title" className="text-xl font-semibold text-gray-900">
                        Start your practice
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Create your real practice in Sena. Your real practice will start with a clean workspace.
                        Demo clients and sandbox records will not transfer.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        Your real practice will start with a clean workspace. Demo clients and sandbox records will not transfer.
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <label htmlFor="sandbox-practice-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Practice name <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="sandbox-practice-name"
                                type="text"
                                value={formData.practiceName}
                                onChange={(e) => updateField('practiceName', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                required
                                disabled={submitting}
                            />
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="sandbox-time-zone" className="block text-sm font-medium text-gray-700 mb-1">
                                Time zone <span className="text-red-500">*</span>
                            </label>
                            <TimeZoneCombobox
                                value={formData.timeZoneId}
                                onChange={(value) => updateField('timeZoneId', value)}
                                className="w-full"
                            />
                            <input id="sandbox-time-zone" className="sr-only" tabIndex={-1} value={formData.timeZoneId} readOnly required />
                        </div>

                        <div>
                            <label htmlFor="sandbox-office-email" className="block text-sm font-medium text-gray-700 mb-1">
                                Office email
                            </label>
                            <input
                                id="sandbox-office-email"
                                type="email"
                                value={formData.officeEmail}
                                onChange={(e) => updateField('officeEmail', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                        <div>
                            <label htmlFor="sandbox-office-phone" className="block text-sm font-medium text-gray-700 mb-1">
                                Office phone
                            </label>
                            <input
                                id="sandbox-office-phone"
                                type="tel"
                                value={formData.officePhone}
                                onChange={(e) => updateField('officePhone', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="sandbox-office-address" className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            <input
                                id="sandbox-office-address"
                                type="text"
                                value={formData.officeAddress}
                                onChange={(e) => updateField('officeAddress', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                        <div>
                            <label htmlFor="sandbox-office-city" className="block text-sm font-medium text-gray-700 mb-1">
                                City
                            </label>
                            <input
                                id="sandbox-office-city"
                                type="text"
                                value={formData.officeCity}
                                onChange={(e) => updateField('officeCity', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                        <div>
                            <label htmlFor="sandbox-office-state" className="block text-sm font-medium text-gray-700 mb-1">
                                State
                            </label>
                            <input
                                id="sandbox-office-state"
                                type="text"
                                value={formData.officeState}
                                onChange={(e) => updateField('officeState', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                        <div>
                            <label htmlFor="sandbox-office-zip" className="block text-sm font-medium text-gray-700 mb-1">
                                ZIP/postal code
                            </label>
                            <input
                                id="sandbox-office-zip"
                                type="text"
                                value={formData.officeZip}
                                onChange={(e) => updateField('officeZip', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] text-sm"
                                disabled={submitting}
                            />
                        </div>

                    </div>

                    {error && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#4f7a64] hover:bg-[#3d6350] rounded-lg transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Creating practice...' : 'Create your real practice'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
